import React, { useCallback, useState } from 'react';
import { LazyComponent } from '@fiverr-private/orca';
import { FilterIcon } from '@fiverr-private/visuals';
import { Divider, Stack } from '@fiverr-private/layout_components';
import { Box } from '@fiverr-private/theme';
import { Modal, ModalContent, ModalTrigger } from '@fiverr-private/overlay';
import { FILTER, FILTERS_LOCATION, tracker } from '@fiverr-private/listing_lib';
import type {
    ActiveSideFiltersType,
    FilterCollectionType,
    SelectedFilter as SelectedFilterType,
} from '@fiverr-private/listing_types';
import { useAppContext } from '../../../context/listingsContext';
import { AppI18n } from '../../../../../../shared/utils/i18n';
import { PillButton } from '../../pill_button';
import { CounterBadge } from '../../counter_badge';
import { calculateActiveFiltersCount } from '../FiltersDrawer/utils/calculateActiveFiltersCount';
import { applyFilters } from '../FiltersDrawer/utils/applyFilters';
import { isSimilarTimezoneActive } from '../../../../utils/timezoneFilter';
import { isOnlineNowSelected } from '../utils';
import {
    FILTERS_MODAL_FULL_SCREEN_BREAKPOINT,
    FILTERS_MODAL_SIZE,
    FILTERS_MODAL_TRACKING_SOURCE,
} from './FiltersModal.constants';

const lazyFiltersModalContent = () =>
    import(/* webpackChunkName: 'listings/FiltersModal' */ './FiltersModalContent/FiltersModalContent').then(
        (module) => ({ default: module.FiltersModalContent })
    );

interface FiltersModalProps {
    filterCollections: FilterCollectionType[];
    activeFilters: ActiveSideFiltersType;
    selectedFiltersHierarchy: SelectedFilterType[];
    isOpen: boolean;
    setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
    numOfResult: number;
    hideDivider?: boolean;
    iconOnly?: boolean;
}

export const FiltersModal = ({
    filterCollections = [],
    activeFilters,
    selectedFiltersHierarchy,
    isOpen,
    setIsOpen,
    numOfResult,
    hideDivider = false,
    iconOnly = false,
}: FiltersModalProps) => {
    const { searchUrl, searcherApiPath, dominateSubCategoryId, similarTimezoneCountries } = useAppContext();
    const [changedFilterCollections, setChangedFilterCollections] = useState(filterCollections);
    // Draft state for similar_timezone, a URL param outside filterCollections. null = untouched.
    const [pendingSimilarTimezone, setPendingSimilarTimezone] = useState<boolean | null>(null);
    // Online now is not part of filterCollections either, so Clear all needs an explicit flag.
    const [pendingOnlineNowClear, setPendingOnlineNowClear] = useState(false);

    const open = () => {
        tracker.trackFilterGroupOpen('all_filters', FILTERS_MODAL_TRACKING_SOURCE);
        setIsOpen(true);
    };

    const close = () => {
        setIsOpen(false);
        setChangedFilterCollections(filterCollections);
        setPendingSimilarTimezone(null);
        setPendingOnlineNowClear(false);
    };

    const applyFiltersHandler = () => {
        applyFilters(changedFilterCollections, activeFilters, {
            pendingSimilarTimezone,
            shouldClearOnlineNow: pendingOnlineNowClear,
            location: FILTERS_LOCATION.FILTERS_MODAL,
        });
        setIsOpen(false);
        setPendingSimilarTimezone(null);
        setPendingOnlineNowClear(false);
    };

    // Pro, Online now and Similar time zone are missing from selectedFiltersHierarchy,
    // but the modal lets users clear them, so the badge counts them too.
    const isProSelected = Boolean(activeFilters[FILTER.PRO.ID]);
    const hasOnlineNowFilter = isOnlineNowSelected(activeFilters);
    const wasSimilarTimezoneActive = isSimilarTimezoneActive(activeFilters);
    const selectedFiltersCount =
        calculateActiveFiltersCount(selectedFiltersHierarchy) +
        (isProSelected ? 1 : 0) +
        (hasOnlineNowFilter ? 1 : 0) +
        (wasSimilarTimezoneActive ? 1 : 0);
    const displayedSimilarTimezoneActive = pendingSimilarTimezone ?? wasSimilarTimezoneActive;
    const isOnlineNowActive = !pendingOnlineNowClear && hasOnlineNowFilter;

    const onClearAll = useCallback(() => {
        setPendingSimilarTimezone(false);
        setPendingOnlineNowClear(true);
    }, []);
    const onToggleSimilarTimezone = useCallback(
        () => setPendingSimilarTimezone((pending) => !(pending ?? wasSimilarTimezoneActive)),
        [wasSimilarTimezoneActive]
    );

    return (
        <Modal
            isOpen={isOpen}
            onOpen={open}
            onClose={close}
            fullScreenBreakpoint={FILTERS_MODAL_FULL_SCREEN_BREAKPOINT}
        >
            <ModalTrigger>
                {iconOnly ? (
                    <Box position="relative" width="fit-content" height="fit-content">
                        <PillButton isActive={selectedFiltersCount > 0} onClick={open} iconOnly>
                            <FilterIcon size="sm" color="currentColor" />
                        </PillButton>
                        {selectedFiltersCount > 0 && (
                            <Box position="absolute" top="-4px" right="-4px">
                                <CounterBadge count={selectedFiltersCount} />
                            </Box>
                        )}
                    </Box>
                ) : (
                    <Stack marginRight="1" gap="4" width="fit-content" height="fit-content">
                        <PillButton isActive={selectedFiltersCount > 0} onClick={open}>
                            <FilterIcon size="sm" color="currentColor" />
                            <AppI18n k="search_perseus.side_filters.filter_row_all_filters_title" />
                            {selectedFiltersCount > 0 && <CounterBadge count={selectedFiltersCount} />}
                        </PillButton>
                        {!hideDivider && <Divider direction="vertical" height="auto" />}
                    </Stack>
                )}
            </ModalTrigger>
            {/* No renderWhenClosed: collapsible content measures zero height inside display:none */}
            <ModalContent closeOnOverlayClick size={FILTERS_MODAL_SIZE}>
                <LazyComponent
                    lazyImport={lazyFiltersModalContent}
                    componentProps={{
                        numOfResult,
                        searchUrl,
                        searcherApiPath,
                        dominateSubCategoryId,
                        changedFilterCollections,
                        setChangedFilterCollections,
                        applyFiltersHandler,
                        onClearAll,
                        isSimilarTimezoneActive: displayedSimilarTimezoneActive,
                        onToggleSimilarTimezone,
                        similarTimezoneCountries,
                        isOnlineNowActive,
                    }}
                />
            </ModalContent>
        </Modal>
    );
};
